<template>
  <div class="loan-emi-schedule">
    <!-- No EMIs message -->
    <!-- Accept Installment Header -->
    <div class="flex justify-between items-center mb-6">
      <h3 class="text-xl font-bold text-gray-800">EMI Schedule</h3>
      <div class="flex justify-end gap-2">
        <v-btn
          color="success"
          depressed
          small
          class="font-weight-black rounded-lg px-6 mr-2"
          @click="exportToExcel"
          :disabled="!emiSchedule.length"
        >
          <v-icon left>mdi-file-excel</v-icon>
          Download
        </v-btn>
        <v-btn
          color="primary"
          depressed
          small
          class="font-weight-black rounded-lg px-6"
          @click="showAcceptInstallment = true"
          :disabled="isLoanSettled"
        >
          <v-icon left>{{ isLoanSettled ? 'mdi-lock' : 'mdi-plus' }}</v-icon>
          {{ isLoanSettled ? 'Loan Settled' : 'Accept Installment' }}
        </v-btn>
      </div>
    </div>

    <!-- Accept Installment Dialog -->
    <v-dialog v-model="showAcceptInstallment" max-width="850px" persistent overlay-opacity="0.8">
      <v-card class="rounded-xl overflow-hidden">
        <v-card-title class="d-flex justify-space-between align-center px-6 py-4 border-b">
          <div class="d-flex align-center">
            <div class="bg-blue-50 p-2 rounded-lg mr-3">
              <v-icon color="primary">mdi-cash-register</v-icon>
            </div>
            <span class="text-lg font-bold text-gray-900 uppercase tracking-widest"
              >EMI Collection</span
            >
          </div>
          <v-btn icon @click="showAcceptInstallment = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-0">
          <div class="p-6">
            <!-- Summary Grid (Compact) -->
            <div
              class="grid grid-cols-2 md:grid-cols-5 gap-4 bg-slate-50 p-4 rounded-lg border border-gray-100 mb-6"
            >
              <div class="flex flex-col">
                <span class="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1"
                  >Due</span
                >
                <span class="text-sm font-black text-gray-900"
                  >₹{{ formatCurrency(paymentSummary.installmentDue) }}</span
                >
              </div>
              <div class="flex flex-col">
                <span class="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1"
                  >Est. LPC</span
                >
                <span class="text-sm font-black text-gray-900"
                  >₹{{ formatCurrency(paymentSummary.estLpc) }}</span
                >
              </div>
              <div class="flex flex-col">
                <span class="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1"
                  >VAS</span
                >
                <span class="text-sm font-black text-gray-900"
                  >₹{{ formatCurrency(paymentSummary.estVas) }}</span
                >
              </div>
              <div class="flex flex-col">
                <span class="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-1"
                  >Charges</span
                >
                <span class="text-sm font-black text-gray-900"
                  >₹{{ formatCurrency(paymentSummary.dueCharges) }}</span
                >
              </div>
              <div class="flex flex-col border-l pl-4">
                <span class="text-[10px] font-bold text-primary uppercase tracking-wider mb-1"
                  >Total</span
                >
                <span class="text-sm font-black text-primary"
                  >₹{{ formatCurrency(paymentSummary.total) }}</span
                >
              </div>
            </div>

            <v-form ref="paymentForm" class="payment-fields-compact">
              <v-row dense>
                <!-- Mode of Payment -->
                <v-col cols="12" md="6" class="pr-2">
                  <div class="mb-1 text-xs font-bold text-gray-600 ml-1 uppercase tracking-tight">
                    Mode of Payment *
                  </div>
                  <v-select
                    v-model="form.mode"
                    :items="['Cash', 'Bank', 'Transfer']"
                    outlined
                    dense
                    hide-details
                    placeholder="Select Mode"
                    class="rounded-lg text-sm"
                  ></v-select>
                </v-col>

                <!-- Ledger / From Account -->
                <v-col cols="12" md="6" class="pl-2">
                  <div class="mb-1 text-xs font-bold text-gray-600 ml-1 uppercase tracking-tight">
                    {{ form.mode === 'Transfer' ? 'From Account' : 'Ledger' }} *
                  </div>
                  <v-autocomplete
                    v-model="form.ledgerId"
                    :items="accounts"
                    item-text="name"
                    item-value="_id"
                    :search-input.sync="form.search"
                    outlined
                    dense
                    hide-details
                    placeholder="Search Account"
                    class="rounded-lg text-sm font-weight-bold"
                  ></v-autocomplete>
                </v-col>

                <!-- Amount -->
                <v-col cols="12" md="6" class="pr-2 mt-4">
                  <div class="mb-1 text-xs font-bold text-gray-600 ml-1 uppercase tracking-tight">
                    Amount *
                  </div>
                  <v-text-field
                    v-model.number="form.amount"
                    type="number"
                    outlined
                    dense
                    hide-details
                    placeholder="0.00"
                    class="rounded-lg text-sm font-weight-black"
                  ></v-text-field>
                </v-col>

                <!-- Collection Charges -->
                <v-col cols="12" md="6" class="pl-2 mt-4">
                  <div class="mb-1 text-xs font-bold text-gray-600 ml-1 uppercase tracking-tight">
                    Collection Charges
                  </div>
                  <v-text-field
                    v-model.number="form.collectionCharges"
                    type="number"
                    outlined
                    dense
                    hide-details
                    placeholder="0.00"
                    class="rounded-lg text-sm"
                  ></v-text-field>
                </v-col>

                <!-- Date -->
                <v-col cols="12" md="6" class="pr-2 mt-4">
                  <div class="mb-1 text-xs font-bold text-gray-600 ml-1 uppercase tracking-tight">
                    Date *
                  </div>
                  <v-text-field
                    v-model="form.date"
                    type="date"
                    outlined
                    dense
                    hide-details
                    class="rounded-lg text-sm"
                  ></v-text-field>
                </v-col>


                <!-- Channel (Bank only) -->
                <v-col v-if="form.mode === 'Bank'" cols="12" md="6" class="pr-2 mt-4">
                  <div class="mb-1 text-xs font-bold text-gray-600 ml-1 uppercase tracking-tight">
                    Channel *
                  </div>
                  <v-select
                    v-model="form.channel"
                    :items="['Cheque', 'NEFT', 'RTGS', 'IMPS', 'UPI']"
                    outlined
                    dense
                    hide-details
                    placeholder="Select Channel"
                    class="rounded-lg text-sm"
                  ></v-select>
                </v-col>

                <!-- Voucher No -->
                <v-col cols="12" md="6" class="pl-2 mt-4">
                  <div class="mb-1 text-xs font-bold text-gray-600 ml-1 uppercase tracking-tight">
                    Voucher No
                  </div>
                  <v-text-field
                    v-model="form.voucherNo"
                    outlined
                    dense
                    hide-details
                    readonly
                    background-color="grey lighten-4"
                    class="rounded-lg text-sm"
                  ></v-text-field>
                </v-col>

                <!-- Comments -->
                <v-col cols="12" class="mt-4">
                  <div class="mb-1 text-xs font-bold text-gray-600 ml-1 uppercase tracking-tight">
                    Comments
                  </div>
                  <v-text-field
                    v-model="form.comments"
                    outlined
                    dense
                    hide-details
                    placeholder="Add collection notes..."
                    class="rounded-lg text-sm"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="px-6 py-4 bg-gray-50 d-flex gap-3">
          <v-spacer></v-spacer>
          <v-btn
            text
            class="normal-case font-weight-bold px-6 border"
            @click="showAcceptInstallment = false"
          >
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            outlined
            class="rounded-lg font-weight-black px-6"
            @click="handleAccept(true)"
          >
            ACCEPT & PRINT
          </v-btn>
          <v-btn
            color="primary"
            depressed
            class="rounded-lg font-weight-black px-10"
            @click="handleAccept(false)"
          >
            ACCEPT
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div v-if="!emiSchedule.length" class="text-center py-8">
      <v-icon size="64" color="grey lighten-1">mdi-calendar-remove</v-icon>
      <div class="mt-4 text-gray-600">
        No EMI schedule available for {{ borrowerName || 'this borrower' }}
      </div>
    </div>

    <v-expansion-panels v-else v-model="expandedPanel">
      <v-expansion-panel
        v-for="(emi, i) in emiSchedule"
        :key="emi._id"
        class="mb-3"
        :class="{ 'bg-red-50/30': emi.isDeleted }"
      >
        <v-expansion-panel-header :class="{ 'opacity-70': emi.isDeleted }">
          <div class="w-full grid grid-cols-9 gap-4 text-center">
            <div>
              <p class="text-sm font-medium text-gray-600">EMI No.</p>
              <div class="flex items-center justify-center">
                <p class="font-semibold" :class="{ 'line-through text-red-600': emi.isDeleted }">
                  {{ i + 1 }}
                </p>
                <v-chip v-if="emi.isDeleted" x-small color="red" dark class="ml-1 px-1 font-bold"
                  >DELETED</v-chip
                >
              </div>
            </div>
            <div>
              <p class="text-sm font-medium text-gray-600">Due Date</p>
              <p class="font-semibold">{{ formatDate(emi.dueDate) }}</p>
              <div
                v-if="getOverdueInfo(emi).isOverdue || getOverdueInfo(emi).isLatePaid"
                class="mt-1"
              >
                <v-chip
                  small
                  outlined
                  label
                  class="font-bold"
                  :color="
                    getOverdueInfo(emi).isLatePaid
                      ? getStatusColor('late_paid').textColor
                      : getStatusColor('overdue').textColor
                  "
                >
                  <v-icon left x-small>
                    {{ getOverdueInfo(emi).isLatePaid ? 'mdi-clock-alert' : 'mdi-alert-circle' }}
                  </v-icon>
                  {{
                    getOverdueInfo(emi).isLatePaid
                      ? getOverdueInfo(emi).latePaidText
                      : getOverdueInfo(emi).overdueText
                  }}
                </v-chip>
              </div>
            </div>
            <div>
              <p class="text-sm font-medium text-gray-600">@Interest</p>
              <p class="font-semibold">{{ emi?.atRateOfInterest }}%</p>
            </div>
            <div :class="{ 'opacity-60': emi.isDeleted }">
              <p class="text-sm font-medium text-gray-600">EMI Amount</p>
              <p class="font-semibold" :class="{ 'line-through': emi.isDeleted }">
                ₹{{ formatCurrency(emi.emiAmount) }}
              </p>
              <div v-if="getOverdueInfo(emi).lateFeeAmount > 0" class="mt-1">
                <v-chip
                  outlined
                  label
                  small
                  class="text-xs font-bold"
                  :color="getStatusColor('late_fee').textColor"
                >
                  Late Fee: ₹{{ formatCurrency(getOverdueInfo(emi).lateFeeAmount) }}
                </v-chip>
              </div>
            </div>
            <div>
              <p class="text-sm font-medium text-gray-600">Principal</p>
              <p class="font-semibold">₹{{ formatCurrency(emi.principalAmount) }}</p>
            </div>
            <div>
              <p class="text-sm font-medium text-gray-600">Interest</p>
              <p class="font-semibold">₹{{ formatCurrency(emi.interestAmount) }}</p>
            </div>
            <div>
              <p class="text-sm font-medium text-gray-600">Principal Outstanding</p>
              <p class="font-semibold">₹{{ formatCurrency(emi.outstandingAmount) }}</p>
            </div>
            <div>
              <p class="text-sm font-medium text-gray-600">PDD Status</p>
              <v-chip small outlined label :color="getPDDStatus(emi.activities)?.color">
                {{ getPDDStatus(emi.activities).status }}
              </v-chip>
            </div>
            <div>
              <p class="text-sm font-medium text-gray-600">EMI Status</p>
              <v-chip
                small
                label
                outlined
                class="font-bold"
                v-if="emi.emiStatus !== 'Minimum Amount Paid'"
                :color="getStatusColor(emi?.emiStatus).textColor"
              >
                {{ emi.emiStatus }}
              </v-chip>
              <div v-else class="mt-1">
                <v-chip
                  small
                  :color="getStatusColor('minimum_amount_paid')?.textColor"
                  class="font-bold"
                  outlined
                  label
                >
                  {{ emi?.emiStatus }}
                </v-chip>
                <div class="mt-1">
                  <v-chip
                    small
                    :color="getStatusColor('minimum_amount_paid')?.textColor"
                    class="font-bold"
                    outlined
                    label
                  >
                    ₹{{ formatCurrency(calculateMinimumAmountForEmi(emi)) }}
                  </v-chip>
                </div>
              </div>
            </div>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <div class="activities-container mt-4">
            <!-- Revenue Share Information -->
            <div v-if="revenueShare && Object.keys(revenueShare).length" class="mb-6">
              <h4 class="text-md font-semibold mb-3">Revenue Breakdown</h4>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <!-- Left Card: Partner & Broker Revenue Share -->
                <div class="p-4 bg-blue-50 rounded-lg border">
                  <h5 class="text-sm font-semibold text-gray-700 mb-3">Profit Details</h5>
                  <div class="flex justify-between space-x-3">
                    <div class="text-center w-1/3">
                      <p class="text-sm font-medium text-gray-600">
                        {{ partner?.partnerName }} (Partner)
                      </p>
                      <p class="text-sm font-bold text-blue-600">
                        ₹{{ formatCurrency(calculatePartnerShare(emi)) }}
                      </p>
                      <p class="text-xs text-gray-500">
                        ({{ revenueShare.hurdleRate }}/{{ emiSchedule.length }} % Hurdle Rate +
                        {{ revenueShare.partnerShare }}% Bonus Profit)
                      </p>
                    </div>
                    <v-divider vertical></v-divider>
                    <div class="text-center w-1/3">
                      <p class="text-sm font-medium text-gray-600">{{ orgName }} (Broker)</p>
                      <p class="text-sm font-bold text-green-600">
                        ₹{{
                          formatCurrency(
                            calculateBrokerShare(
                              emi,
                              revenueShare,
                              getTotalPrincipal(),
                              emiSchedule?.length
                            )
                          )
                        }}
                      </p>
                      <p class="text-xs text-gray-500">
                        ({{ revenueShare.brokerShare }}% Bonus Profit)
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Right Card: Actual Amount Calculation -->
                <div class="p-4 bg-green-50 rounded-lg border">
                  <h5 class="text-sm font-semibold text-gray-700 mb-3">Amount Distribution</h5>
                  <div class="space-y-3">
                    <div class="text-center pt-2">
                      <p class="text-sm text-gray-600">
                        Total EMI Amount - Broker Profit = Partner EMI
                      </p>
                      <p class="text-sm font-medium text-gray-700">
                        ₹{{ formatCurrency(emi.emiAmount) }} - ₹{{
                          formatCurrency(calculateBrokerShare(emi))
                        }}
                        =
                        <span class="text-orange-600">
                          ₹{{ formatCurrency(calculateActualPartnerAmount(emi)) }}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Payment details section -->
            <div v-if="emi.emiPayments && emi.emiPayments.length" class="mb-6">
              <h4 class="text-md font-semibold mb-3">Payment History</h4>
              <div class="border rounded-lg overflow-hidden shadow-sm">
                <v-simple-table dense>
                  <template v-slot:default>
                    <thead>
                      <tr class="bg-gray-100">
                        <th class="text-left px-4 py-3 font-bold text-gray-700">Date</th>
                        <th class="text-left px-4 py-3 font-bold text-gray-700">Receipt No</th>
                        <th class="text-right px-4 py-3 font-bold text-gray-700">Amount</th>
                        <th class="text-right px-4 py-3 font-bold text-gray-700">Principal</th>
                        <th class="text-right px-4 py-3 font-bold text-gray-700">Late Fee</th>
                        <th class="text-right px-4 py-3 font-bold text-gray-700">Coll. Charges</th>
                        <th class="text-right px-4 py-3 font-bold text-gray-700">LPC Due</th>
                        <th class="text-right px-4 py-3 font-bold text-gray-700">Int. O/S</th>
                        <th class="text-right px-4 py-3 font-bold text-gray-700">Advance</th>
                        <th class="text-center px-4 py-3 font-bold text-gray-700">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(payment, pIdx) in emi.emiPayments"
                        :key="pIdx"
                        :class="[
                          'hover:bg-gray-50 transition-colors',
                          payment.isDeleted ? 'bg-red-50 text-gray-500 opacity-70' : ''
                        ]"
                      >
                        <td class="px-4 py-3">
                          <div class="flex items-center space-x-2">
                            <span :class="{ 'strike-through-text': payment.isDeleted }">
                              {{ formatDate(payment.date) }}
                            </span>
                            <v-chip
                              v-if="payment.isDeleted"
                              x-small
                              color="red lighten-4"
                              text-color="red darken-4"
                              class="font-bold"
                            >
                              DELETED
                            </v-chip>
                          </div>
                        </td>
                        <td class="px-4 py-3 font-medium text-gray-700">
                          <span :class="{ 'strike-through-text': payment.isDeleted }">
                            {{ payment.voucherNo || '-' }}
                          </span>
                        </td>
                        <td
                          class="text-right px-4 py-3 font-bold"
                          :class="payment.isDeleted ? 'text-gray-500' : 'text-blue-700'"
                        >
                          <span :class="{ 'strike-through-text': payment.isDeleted }">
                            ₹{{ formatCurrency(payment.amount) }}
                          </span>
                        </td>
                        <td
                          class="text-right px-4 py-3"
                          :class="{ 'strike-through-text': payment.isDeleted }"
                        >
                          ₹{{ formatCurrency(payment.principalAmount) }}
                        </td>
                        <td
                          class="text-right px-4 py-3"
                          :class="{ 'strike-through-text': payment.isDeleted }"
                        >
                          ₹{{ formatCurrency(payment.latePaymentAmount) }}
                        </td>
                        <td
                          class="text-right px-4 py-3"
                          :class="{ 'strike-through-text': payment.isDeleted }"
                        >
                          ₹{{ formatCurrency(payment.collectionCharges) }}
                        </td>
                        <td
                          class="text-right px-4 py-3"
                          :class="{ 'strike-through-text': payment.isDeleted }"
                        >
                          ₹{{ formatCurrency(payment.lpcDueAmount) }}
                        </td>
                        <td
                          class="text-right px-4 py-3"
                          :class="{ 'strike-through-text': payment.isDeleted }"
                        >
                          ₹{{ formatCurrency(payment.interestOutStandingAmount) }}
                        </td>
                        <td
                          class="text-right px-4 py-3"
                          :class="{ 'strike-through-text': payment.isDeleted }"
                        >
                          ₹{{ formatCurrency(payment.receiveAdvanceAmount) }}
                        </td>
                        <td class="text-center px-4 py-3">
                          <div
                            class="d-flex align-center justify-center gap-1"
                            v-if="!payment.isDeleted"
                          >
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                                <v-menu offset-y left>
                                  <template v-slot:activator="{ on: menuOn, attrs: menuAttrs }">
                                    <v-btn
                                      icon
                                      small
                                      color="primary"
                                      v-bind="{ ...tooltipAttrs, ...menuAttrs }"
                                      v-on="{ ...tooltipOn, ...menuOn }"
                                      :loading="isGeneratingPDF === payment._id"
                                    >
                                      <v-icon small>mdi-printer</v-icon>
                                    </v-btn>
                                  </template>
                                  <v-list dense>
                                    <v-list-item @click="generateReceipt(emi, payment, i, 'view')">
                                      <v-list-item-icon class="mr-2">
                                        <v-icon small>mdi-eye</v-icon>
                                      </v-list-item-icon>
                                      <v-list-item-title>View Slip</v-list-item-title>
                                    </v-list-item>
                                    <v-list-item
                                      @click="generateReceipt(emi, payment, i, 'download')"
                                    >
                                      <v-list-item-icon class="mr-2">
                                        <v-icon small>mdi-download</v-icon>
                                      </v-list-item-icon>
                                      <v-list-item-title>Download Slip</v-list-item-title>
                                    </v-list-item>
                                  </v-list>
                                </v-menu>
                              </template>
                              <span>Receipt Options</span>
                            </v-tooltip>

                            <v-tooltip bottom>
                              <template v-slot:activator="{ on, attrs }">
                                <v-btn
                                  icon
                                  small
                                  color="error"
                                  v-bind="attrs"
                                  v-on="on"
                                  @click="confirmRevert(emi, payment)"
                                >
                                  <v-icon small>mdi-delete-outline</v-icon>
                                </v-btn>
                              </template>
                              <span>Revert Payment</span>
                            </v-tooltip>
                          </div>
                          <v-icon v-else color="grey lighten-1">mdi-cancel</v-icon>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </div>
            </div>

            <div class="flex justify-between items-center mb-3">
              <h4 class="text-md font-semibold">
                {{ emi.activities && emi.activities.length ? 'Requirements' : '' }}
              </h4>
              <v-btn
                small
                color="primary"
                outlined
                @click="openEditEmiDialog(emi._id)"
                :disabled="isLoanSettled"
              >
                <v-icon left small>mdi-pencil</v-icon>
                Edit EMI Details
              </v-btn>
            </div>

            <template v-if="emi.activities && emi.activities.length">
              <div
                v-for="(group, stepName) in groupActivitiesByStepName(emi.activities)"
                :key="stepName"
              >
                <h5 class="text-sm font-bold text-gray-500 mb-2 mt-4">{{ stepName }}</h5>
                <div
                  v-for="activity in group"
                  :key="activity._id"
                  class="activity-item mb-3 border rounded-md bg-gray-50 dark-bg-custom"
                >
                  <div class="flex justify-between items-center pl-2 pr-2">
                    <div class="flex items-center justify-between w-full">
                      <p class="font-medium text-sm pt-3">{{ activity.activityType }}</p>
                      <div class="flex items-center space-x-2 justify-end">
                        <p class="mb-0 text-xs text-gray-600">
                          Last Updated: {{ formatDateTime(activity.updatedAt) }}
                        </p>
                        <v-chip
                          small
                          label
                          outlined
                          :color="getActivityStatusColor(activity.status)"
                        >
                          {{ activity.status }}
                        </v-chip>
                      </div>
                    </div>
                  </div>
                  <div v-if="activity.activityDoc" class="mt-3">
                    <div class="flex items-center">
                      <v-icon size="20" color="primary" class="mr-2"
                        >mdi-file-document-outline</v-icon
                      >
                      <a
                        :href="activity.activityDoc"
                        target="_blank"
                        class="text-blue-600 hover:underline"
                      >
                        View Document
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Edit EMI Dialog -->
    <AddEditEmi
      v-model="editEmiDialog"
      :emi-data="selectedEmi"
      :is-loan-settled="isLoanSettled"
      @save-emi="handleEmiUpdate"
      @show-notification="$emit('show-notification', $event)"
    />

    <!-- Hidden Receipt for Printing -->
    <EmiPaymentReceipt
      ref="receiptRef"
      v-if="receiptData.payment"
      :payment="receiptData.payment"
      :customer-name="receiptData.customerName"
      :agreement-date="receiptData.agreementDate"
      :agreement-no="receiptData.agreementNo"
      :emi-no="receiptData.emiNo"
      :total-emis="emiSchedule.length"
      :emi-due-date="receiptData.emiDueDate"
      :emi-amount="receiptData.emiAmount"
      :cashier-name="receiptData.cashierName"
    />
    <!-- Revert Confirmation Dialog -->
    <v-dialog v-model="revertDialog" max-width="450px">
      <v-card class="rounded-xl overflow-hidden">
        <v-card-title class="bg-red-50 text-red-800 py-4 px-6 font-bold">
          Revert Installment Payment?
        </v-card-title>
        <v-card-text class="pa-6 text-gray-600">
          Are you sure you want to revert the payment amount of
          <span class="font-bold text-gray-800"
            >₹{{ formatCurrency(selectedPaymentToRevert?.amount || 0) }}</span
          >
          with Voucher No
          <span class="font-bold text-gray-800"
            >"{{ selectedPaymentToRevert?.voucherNo || '-' }}"</span
          >? This will revert the status and balances of all adjusted EMIs and soft-delete the
          accounting entries.
        </v-card-text>
        <v-card-actions class="pa-4 bg-gray-50">
          <v-spacer></v-spacer>
          <v-btn text @click="revertDialog = false" class="normal-case">Cancel</v-btn>
          <v-btn
            color="error"
            depressed
            @click="submitRevert"
            :loading="reverting"
            class="normal-case rounded-lg px-6 font-bold shadow-sm"
          >
            Confirm Revert
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { calculateOverdueDays, calculateBrokerShareAmount } from '@/utils/common'
import { chipStatusColors } from '@/utils/workforce/statusColors'
import AddEditEmi from './VisitModules/AddEditEmi.vue'
import EmiPaymentReceipt from './EmiPaymentReceipt.vue'
import html2pdf from 'html2pdf.js'
import { debounce } from 'lodash'
import * as XLSX from 'xlsx'

export default {
  name: 'LoanEmiScheduleView',
  components: {
    AddEditEmi,
    EmiPaymentReceipt
  },
  props: {
    emiSchedule: {
      type: Array,
      required: true
    },
    borrowerId: {
      type: String,
      default: null
    },
    revenueShare: {
      type: Object,
      default: () => {}
    },
    partner: {
      type: Object,
      default: () => {}
    },
    selectedCase: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      orgName: this.$storage.getUniversal('organization_code'),
      expandedPanel: null,
      editEmiDialog: false,
      selectedEmi: {},
      isGeneratingPDF: null,
      receiptData: {
        payment: null,
        customerName: '',
        agreementDate: '',
        agreementNo: '',
        emiNo: 1,
        emiDueDate: '',
        emiAmount: 0,
        cashierName: ''
      },
      showAcceptInstallment: false,
      accounts: [],
      revertDialog: false,
      selectedPaymentToRevert: null,
      reverting: false,
      form: {
        mode: 'Cash',
        ledgerId: null,
        amount: 0,
        collectionCharges: 0,
        date: new Date().toISOString().substr(0, 10),
        instrumentNo: '',
        voucherNo: '',
        comments: '',
        sendSms: true,
        channel: null,
        search: ''
      }
    }
  },
  computed: {
    borrowerName() {
      return this.borrowerId || 'Unknown Borrower'
    },
    paymentSummary() {
      const unpaidEmis = this.emiSchedule.filter(
        (e) => !['Paid', 'Late', 'Late Paid', 'Fully Paid'].includes(e.emiStatus) && !e.isDeleted
      )

      let installmentDue = 0
      unpaidEmis.forEach((emi) => {
        const emiPaid = (emi.emiPayments || [])
          .filter((p) => !p.isDeleted)
          .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0)
        installmentDue += Math.max(0, (parseFloat(emi.emiAmount) || 0) - emiPaid)
      })

      let estLpc = 0
      const today = this.$dayjs().startOf('day')
      const loanDetails = this.selectedCase?.loanDetails
      const feeType = loanDetails?.lateFeeType || 'Fixed'
      const feeVal =
        loanDetails?.latePaymentFee !== undefined && loanDetails?.latePaymentFee !== null
          ? parseFloat(loanDetails.latePaymentFee)
          : 0
      const paidStatuses = ['Paid', 'Late', 'Late Paid', 'Minimum Amount Paid']

      this.emiSchedule
        .filter((e) => !e.isDeleted)
        .forEach((emi) => {
          const activePayments = (emi.emiPayments || []).filter((p) => !p.isDeleted)
          const receipt = activePayments.reduce(
            (sum, p) => sum + (parseFloat(p.latePaymentAmount) || 0),
            0
          )

          let totalLpi = 0
          const dueDate = this.$dayjs(emi.dueDate).startOf('day')

          const isMonthlyLogic = feeVal === 600

          const calcLpcFee = (days, refDate) => {
            if (!feeVal || days <= 0) return 0
            if (feeType === 'Fixed' || feeType === 'Flat') {
              if (isMonthlyLogic) {
                const diffMonths = this.$dayjs(refDate).diff(dueDate, 'month')
                const targetDatePlusMonths = this.$dayjs(dueDate).add(diffMonths, 'month')
                const extraDays = this.$dayjs(refDate).diff(targetDatePlusMonths, 'day')
                const chargeableMonths = Math.max(1, diffMonths + (extraDays >= 15 ? 1 : 0))
                return feeVal * chargeableMonths
              }
              return feeVal * days
            }
            if (feeType === 'Percentage') {
              const year = this.$dayjs(refDate).year()
              const yearDays = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0) ? 366 : 365
              return (parseFloat(emi.emiAmount || 0) * feeVal * (days / yearDays)) / 100
            }
            return 0
          }

          if (emi.emiStatus === 'Paid') {
            // Paid on time — no LPC
            totalLpi = 0
          } else if (emi.emiStatus === 'Late' || emi.emiStatus === 'Late Paid' || emi.emiStatus === 'Minimum Amount Paid') {
            // Paid but after due date — calculate from actual payment date
            const payDate = emi.paymentDate
              ? this.$dayjs(emi.paymentDate).startOf('day')
              : (activePayments[0]?.date ? this.$dayjs(activePayments[0].date).startOf('day') : dueDate)
            const lateDays = Math.max(0, payDate.diff(dueDate, 'day'))
            totalLpi = calcLpcFee(lateDays, payDate.toDate())
          } else {
            // Pending / Overdue: calculate dynamically from today
            if (today.isAfter(dueDate)) {
              const lateDays = today.diff(dueDate, 'day')
              totalLpi = calcLpcFee(lateDays, today.toDate())
            }
          }

          totalLpi = Math.max(totalLpi, receipt)

          estLpc += Math.max(0, totalLpi - receipt)
        })

      const estVas = (this.selectedCase?.loanDetails?.additionalCharges || [])
        .filter(
          (c) =>
            !c.isDeleted &&
            !c.isDeduction &&
            c.chargeType === 'CHARGE' &&
            !['Paid', 'Fully Paid'].includes(c.status)
        )
        .reduce((sum, charge) => {
          const net =
            (parseFloat(charge.amount) || 0) -
            (parseFloat(charge.metaData?.discountAmount) || 0) -
            (parseFloat(charge.metaData?.waiverAmount) || 0)
          const unpaid = Math.max(0, net - (parseFloat(charge.paidAmount) || 0))
          return sum + unpaid
        }, 0)
      const dueCharges = 0.0

      const total = installmentDue + estLpc + estVas + dueCharges

      return {
        installmentDue,
        estLpc,
        estVas,
        dueCharges,
        total
      }
    },
    isLoanSettled() {
      const status =
        this.selectedCase?.loanDetails?.currentStatus || this.selectedCase?.status || ''
      return ['Settled', 'Completed', 'Closed', 'Foreclosed'].includes(status)
    }
  },
  watch: {
    'paymentSummary.total': {
      handler(newVal) {
        if (this.form.amount === 0) {
          this.form.amount = newVal
        }
      },
      immediate: true
    },
    'form.mode': {
      handler() {
        this.fetchAccounts()
      },
      immediate: true
    },
    'form.search': {
      handler(val) {
        if (val) {
          this.debouncedFetchAccounts()
        }
      }
    }
  },
  created() {
    this.debouncedFetchAccounts = debounce(this.fetchAccounts, 500)
  },
  async mounted() {
    // Initial fetch handled by immediate watcher on form.mode
  },
  methods: {
    async fetchAccounts() {
      const mode = this.form.mode
      const modeMapping = {
        Cash: 2194,
        Bank: 2195,
        Transfer: 2190
      }
      const ledgerGroupClientId = modeMapping[mode]

      if (!ledgerGroupClientId) {
        this.accounts = []
        return
      }

      try {
        const params = {
          LedgerGroupClientId: ledgerGroupClientId,
          restrictionTypeId: 1
        }
        if (this.form.search) {
          params.name = this.form.search
        }
        const response = await this.$axios.get('/orgSetup/accounts-management', { params })
        this.accounts = response.data.accountsManagements || []
      } catch (error) {
        console.error('Error fetching accounts:', error)
      }
    },
    async handleAccept(print = false) {
      // Basic validation
      if (!this.form.ledgerId || !this.form.amount) {
        this.$emit('show-notification', { message: 'Please fill required fields', color: 'error' })
        return
      }

      const payload = {
        ...this.form,
        borrowerId: this.borrowerId,
        caseId: this.selectedCase?._id
      }

      console.log('Accepting payment:', payload)

      // Emit event to parent to handle the actual API call
      this.$emit('accept-installment', { payload, print })

      // Reset form if success
      this.showAcceptInstallment = false
      this.$emit('show-notification', {
        message: 'Installment accepted successfully',
        color: 'success'
      })
    },

    groupActivitiesByStepName(activities) {
      if (!activities || !activities.length) return {}
      return activities.reduce((acc, activity) => {
        const step = activity.activityStepName || 'Other'
        if (!acc[step]) acc[step] = []
        acc[step].push(activity)
        return acc
      }, {})
    },
    formatDate(dateString) {
      return this.$dayjs(dateString).format('DD MMM YYYY')
    },

    formatDateTime(dateString) {
      return this.$dayjs(dateString).format('DD MMM YYYY, hh:mm A')
    },

    formatCurrency(value) {
      return new Intl.NumberFormat('en-IN').format(value)
    },
    getPDDStatus(activities) {
      if (activities?.length) {
        const isAllStepsCompleted = activities.every((activity) => activity.status === 'Completed')
        return isAllStepsCompleted
          ? { status: 'Completed', color: this.getStatusColor('completed')?.textColor }
          : { status: 'Not Completed', color: this.getStatusColor('not_completed')?.textColor }
      }
      return { status: 'No PDD Steps', color: 'grey' }
    },

    getStatusColor(status) {
      return chipStatusColors[status?.toLowerCase()]
    },

    getOverdueInfo(emi) {
      return calculateOverdueDays(
        emi.dueDate,
        emi.emiStatus,
        emi.paymentDate,
        emi.emiAmount,
        this.selectedCase?.loanDetails?.lateFeeType || emi.lateFeeType || 'Fixed',
        this.selectedCase?.loanDetails?.latePaymentFee !== undefined
          ? parseFloat(this.selectedCase.loanDetails.latePaymentFee)
          : emi.lateFeeValue !== undefined
          ? parseFloat(emi.lateFeeValue)
          : 0,
        this.selectedCase?.loanDetails?.scheme || null
      )
    },

    getActivityStatusColor(status) {
      switch (status) {
        case 'Completed':
          return this.getStatusColor('completed')?.textColor
        case 'Not Completed':
          return this.getStatusColor('not_completed')?.textColor
        case 'In Progress':
          return this.getStatusColor('inProgress')?.textColor
        default:
          return 'grey darken-2'
      }
    },

    openEditEmiDialog(emiId) {
      const emi = this.emiSchedule.find((e) => e._id === emiId)
      if (!emi) return

      this.selectedEmi = emi
      this.editEmiDialog = true
    },

    handleEmiUpdate(payload) {
      // Emit event to parent component to handle the update
      this.$emit('update-emi', payload)
    },

    calculateMinimumAmountForEmi(emi) {
      const interestAmount = parseFloat(emi.interestAmount) || 0
      const overdueInfo = this.getOverdueInfo(emi)
      const lateFeeAmount = overdueInfo.lateFeeAmount || 0
      return interestAmount + lateFeeAmount
    },

    calculatePartnerShare(emi) {
      return emi.interestAmount - this.calculateBrokerShare(emi)
    },
    calculateBrokerShare(emi) {
      return calculateBrokerShareAmount(
        emi,
        this.revenueShare,
        this.getTotalPrincipal(),
        this.emiSchedule?.length
      )
    },
    getTotalPrincipal() {
      if (this.emiSchedule.length > 0) {
        const firstEmi = this.emiSchedule[0]
        return parseFloat(firstEmi.outstandingAmount) || 0
      }
      return 0
    },

    calculateActualPartnerAmount(emi) {
      const emiAmount = parseFloat(emi.emiAmount) || 0
      const brokerAmount = this.calculateBrokerShare(emi)
      return emiAmount - brokerAmount
    },

    async generateReceipt(emi, payment, emiIndex, mode = 'download') {
      if (!this.selectedCase) {
        this.$emit('show-notification', {
          message: 'Case details not available for receipt generation',
          color: 'error'
        })
        return
      }

      this.isGeneratingPDF = payment._id || Math.random().toString()

      // Prepare data for receipt
      this.receiptData = {
        payment: payment,
        customerName: this.selectedCase.customer?.customerFullName || 'Unknown',
        agreementDate: this.selectedCase.loanDetails?.sanctionDate || '',
        agreementNo: this.selectedCase.caseNo || '',
        emiNo: emiIndex + 1,
        totalEmis: this.emiSchedule.length,
        emiDueDate: emi.dueDate,
        emiAmount: parseFloat(emi.emiAmount) || 0,
        cashierName: payment.processedBy || ''
      }

      // Allow some time for DOM update
      await this.$nextTick()

      const element = this.$refs.receiptRef.getHtmlElement()
      const opt = {
        margin: 0,
        filename: `Receipt_${this.receiptData.agreementNo}_EMI_${this.receiptData.emiNo}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, letterRendering: true, scrollY: 0 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' }
      }

      try {
        const worker = html2pdf().set(opt).from(element)

        if (mode === 'download') {
          await worker.save()
          this.$emit('show-notification', {
            message: 'Receipt downloaded successfully',
            color: 'success'
          })
        } else {
          // View mode: Generate blob and open in new tab
          const pdfBlobUrl = await worker.output('bloburl')
          window.open(pdfBlobUrl, '_blank')
        }
      } catch (error) {
        console.error('Error generating PDF receipt:', error)
        this.$emit('show-notification', {
          message: 'Error generating receipt PDF',
          color: 'error'
        })
      } finally {
        this.isGeneratingPDF = null
      }
    },
    exportToExcel() {
      try {
        const data = this.emiSchedule.map((emi, index) => {
          const overdueInfo = this.getOverdueInfo(emi)
          return {
            'EMI No.': index + 1,
            'Due Date': this.formatDate(emi.dueDate),
            'Interest Rate (%)': emi.atRateOfInterest,
            'EMI Amount': parseFloat(Number(emi.emiAmount || 0).toFixed(2)),
            Principal: parseFloat(Number(emi.principalAmount || 0).toFixed(2)),
            Interest: parseFloat(Number(emi.interestAmount || 0).toFixed(2)),
            'Principal Outstanding': parseFloat(Number(emi.outstandingAmount || 0).toFixed(2)),
            'Late Fee': parseFloat(Number(overdueInfo.lateFeeAmount || 0).toFixed(2)),
            'EMI Status': emi.emiStatus,
            'PDD Status': this.getPDDStatus(emi.activities).status,
            'Payment Date': emi.paymentDate ? this.formatDate(emi.paymentDate) : 'N/A'
          }
        })

        const worksheet = XLSX.utils.json_to_sheet(data)
        const workbook = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(workbook, worksheet, 'EMI Schedule')

        // Generate dynamic filename
        const filename = `EMI_Schedule_${
          this.selectedCase?.caseNo || 'Export'
        }_${new Date().getTime()}.xlsx`

        XLSX.writeFile(workbook, filename)

        this.$emit('show-notification', {
          message: 'EMI Schedule exported successfully',
          color: 'success'
        })
      } catch (error) {
        console.error('Error exporting to Excel:', error)
        this.$emit('show-notification', {
          message: 'Error exporting EMI schedule',
          color: 'error'
        })
      }
    },
    confirmRevert(emi, payment) {
      this.selectedPaymentToRevert = {
        ...payment,
        emiId: emi._id
      }
      this.revertDialog = true
    },
    submitRevert() {
      if (!this.selectedPaymentToRevert) return
      this.reverting = true
      this.$emit('revert-installment', {
        voucherNo: this.selectedPaymentToRevert.voucherNo
      })
      this.revertDialog = false
      this.reverting = false
    }
  }
}
</script>

<style scoped>
.loan-emi-schedule {
  border-radius: 5px;
  padding: 20px;
}

.activity-item {
  transition: all 0.2s ease;
}

.activity-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.dark-mode .loan-emi-schedule {
  background-color: #1e1e1e;
  color: #e0e0e0;
}

.strike-through-text {
  text-decoration: line-through;
}
</style>
